import {
  Card,
  Icon,
  Media,
  MediaWithViewer,
  Space,
  SubText,
  WithPopover,
} from '@procurify/ui'

import {
  DataTable,
  configureColumn as col,
  type RowCellMenuListItemProps,
} from '@webapp/components/DataTable'
import { CreditCardLabel } from '@webapp/core/components/CreditCardLabel'
import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExpenseTypeSummary } from './components/ExpenseTypeSummary/ExpenseTypeSummary'
import messages from './messages'
import { NotesRowStyled } from './styles/NotesRowStyled'
import { ExpensePaymentTypes } from '../../entities/enums/ExpensePaymentTypeEnum'
import { type ExpenseItem } from '../../entities/ExpenseItem'
import { ExpenseTypeLabel } from '../ExpenseTypeLabel'

export interface IExpenseItemTableProps {
  items: ExpenseItem[]
  isLoading?: boolean
  loaderRows?: number
  menuListItems?: (
    data: ExpenseItem,
    rowIndex: number
  ) => RowCellMenuListItemProps[]
  onAttachmentClick?: (item: ExpenseItem, itemIndex: number) => void
}

export const ExpenseItemTable = ({
  items,
  isLoading = false,
  loaderRows,
  menuListItems,
  onAttachmentClick,
}: IExpenseItemTableProps) => {
  const intl = useProcurifyIntl()
  const formatMedia = useFormatMedia()

  return (
    <DataTable<ExpenseItem>
      id='expense-item-table'
      data={items}
      isLoading={isLoading}
      loaderRows={loaderRows}
      aria-label={intl.formatMessage(messages.tableLabel)}
      emptyStateText={intl.formatMessage(messages.emptyStateText)}
      customizableColumns={false}
      menuListItems={menuListItems}
      renderAdditionalRows={(item) => {
        if (!item.hasNotes) return []

        return [
          [
            {
              colSpan: 99,
              render: (
                <NotesRowStyled>
                  <Space direction='horizontal'>
                    <Space direction='horizontal' size='small'>
                      <Icon icon='message' size={18} />
                      <SubText bold>
                        {intl.formatMessage(messages.notesPrefix)}
                      </SubText>
                    </Space>
                    <SubText breakWord>{item.notes}</SubText>
                  </Space>
                </NotesRowStyled>
              ),
            },
          ],
        ]
      }}
      columns={[
        col('BASIC', {
          width: 1,
          label: '',
          locked: true,
          getter: (data, rowIndex) => {
            return rowIndex + 1
          },
          loaderWidth: 10,
        }),
        col('BASIC', {
          label: intl.formatMessage(messages.receiptLabel),
          width: 1,
          loaderWidth: 10,
          getter: (data, index) => {
            if (!data.attachment) return null

            return onAttachmentClick ? (
              <Media
                src={formatMedia(data.attachment)}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onAttachmentClick(data, index)
                }}
              />
            ) : (
              <MediaWithViewer src={formatMedia(data.attachment)} />
            )
          },
        }),
        col('BASIC', {
          label: intl.formatMessage(messages.vendorNameLabel),
          getter: (data) => {
            return data.merchant
          },
        }),
        col('BUDGET_ACCOUNTCODE', {
          label: intl.formatMessage(messages.accountCodeLabel),
          getter: (data) => {
            return {
              budget: data.account
                ? {
                    accountCode: data.account.accountCode.code,
                    description: data.account.accountCode.description,
                  }
                : null,
              hasMultiple: false,
            }
          },
        }),
        col('BASIC', {
          label: intl.formatMessage(messages.expenseTypeLabel),
          getter: (data) => {
            return (
              <WithPopover
                content={
                  <Card.Content>
                    <ExpenseTypeSummary item={data} />
                  </Card.Content>
                }
              >
                <ExpenseTypeLabel type={data.expenseType} />
              </WithPopover>
            )
          },
        }),
        col('DATE', {
          width: 0,
          label: intl.formatMessage(messages.dateLabel),
          getter: (data) => {
            return {
              date: data.transactionDate || null,
            }
          },
        }),
        col('BASIC', {
          label: intl.formatMessage(messages.paymentLabel),
          getter: (data) => {
            if (data.paymentCard) {
              return (
                <CreditCardLabel
                  type={data.paymentCard.vendor}
                  last4Digits={data.paymentCard.last4Digits}
                  name={data.paymentCard.name}
                />
              )
            }
            const paymentTypeMessageMap: Partial<
              Record<number, (typeof messages)[keyof typeof messages]>
            > = {
              [ExpensePaymentTypes.CASH]: messages.paymentTypeCash,
              [ExpensePaymentTypes.CHEQUE]: messages.paymentTypeCheque,
              [ExpensePaymentTypes.OTHER]: messages.paymentTypeOther,
            }
            const msg = paymentTypeMessageMap[data.paymentType]
            return msg ? intl.formatMessage(msg) : '--'
          },
        }),
        col('BASIC', {
          label: intl.formatMessage(messages.reimbursableLabel),
          width: 1,
          getter: (data) => {
            return data.reimbursable
              ? intl.formatMessage(messages.positiveReimburseValue)
              : intl.formatMessage(messages.negativeReimburseValue)
          },
        }),
        col('PRICE', {
          label: intl.formatMessage(messages.taxLabel),
          getter: (data) => {
            return {
              price: data.taxAmount,
              currencyName: data.currency.name,
              renderZeroPrice: true,
            }
          },
        }),
        col('PRICE', {
          label: intl.formatMessage(messages.totalAmountLabel),
          getter: (data) => {
            return {
              price: data.amount,
              currency: data.currency,
              renderZeroPrice: true,
            }
          },
        }),
      ]}
    />
  )
}
